import React from 'react';
import { useController, useWatch } from 'react-hook-form';
import type { AgentConfigurationCardProps } from '../../types/features/storeDetails/agentConfigurationCard';

type UseAgentConfigurationCardLogicParams = Pick<
  AgentConfigurationCardProps,
  | 'control'
  | 'setValue'
  | 'posType'
  | 'selectedPosType'
  | 'posTypeOptions'
  | 'onPosTypeChange'
  | 'onFetchLatestLogs'
>;

type LogsFetchState = 'idle' | 'fetching' | 'updated';

export const normalizeAgentPosType = (posType: string) => {
  const normalized = posType.trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized === 'gilbarcopassportpjr') return 'gilbarco';
  if (normalized === 'verifoneruby2') return 'verifone';
  return null;
};

export const useAgentConfigurationCardLogic = ({
  control,
  setValue,
  posType,
  selectedPosType,
  posTypeOptions,
  onPosTypeChange,
  onFetchLatestLogs,
}: UseAgentConfigurationCardLogicParams) => {
  const [logsFetchState, setLogsFetchState] = React.useState<LogsFetchState>('idle');
  const [posTypeMenuOpen, setPosTypeMenuOpen] = React.useState(false);
  const fetchTimeoutRef = React.useRef<number | null>(null);

  const readPath = useWatch({ control, name: 'posConfig.ReadPath' });
  const writePath = useWatch({ control, name: 'posConfig.WritePath' });
  const { field: heartbeatField, fieldState: heartbeatFieldState } = useController({
    control,
    name: 'Heartbeat.IntervalMinutes',
  });

  const effectivePosType = React.useMemo(() => (selectedPosType || posType || '').trim(), [posType, selectedPosType]);
  const agentPosType = normalizeAgentPosType(effectivePosType);
  const isGilbarcoPassportPjr = agentPosType === 'gilbarco';
  const isVerifoneRuby2 = agentPosType === 'verifone';

  const showPosAgentFields = isGilbarcoPassportPjr || isVerifoneRuby2;
  const showGilbarcoFields = isGilbarcoPassportPjr;
  const showScannerProxyFields = showPosAgentFields;
  const showVerifoneFields = isVerifoneRuby2;

  const posTypeLabel = effectivePosType && effectivePosType !== 'n/a' ? effectivePosType : 'Not Available';
  const selectedPosValue = effectivePosType || '';
  const hasSelectedOption = React.useMemo(
    () => posTypeOptions.some((opt) => opt === selectedPosValue),
    [posTypeOptions, selectedPosValue],
  );

  const readPathHasValue = Boolean((readPath || '').trim());
  const writePathHasValue = Boolean((writePath || '').trim());
  const previousReadPathHasValueRef = React.useRef(readPathHasValue);
  const previousWritePathHasValueRef = React.useRef(writePathHasValue);

  React.useEffect(() => {
    const previouslyHadReadPath = previousReadPathHasValueRef.current;
    previousReadPathHasValueRef.current = readPathHasValue;

    if (previouslyHadReadPath && !readPathHasValue) {
      setValue('PosDataUpload.Enabled', false, { shouldDirty: true, shouldValidate: true });
    }
  }, [readPathHasValue, setValue]);

  React.useEffect(() => {
    const previouslyHadWritePath = previousWritePathHasValueRef.current;
    previousWritePathHasValueRef.current = writePathHasValue;

    if (previouslyHadWritePath && !writePathHasValue) {
      setValue('PosDataDownload.Enabled', false, { shouldDirty: true, shouldValidate: true });
    }
  }, [writePathHasValue, setValue]);

  React.useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  const handleGetLatestLogs = React.useCallback(async () => {
    if (logsFetchState === 'fetching') return;

    setLogsFetchState('fetching');
    try {
      await onFetchLatestLogs();

      if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = window.setTimeout(() => {
        setLogsFetchState('idle');
      }, 3000);
      setLogsFetchState('updated');
    } catch {
      setLogsFetchState('idle');
    }
  }, [logsFetchState, onFetchLatestLogs]);

  const handlePosTypeSelect = React.useCallback(
    (value: string) => {
      onPosTypeChange(value);
      setPosTypeMenuOpen(false);
    },
    [onPosTypeChange],
  );

  const handleHeartbeatIntervalChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) {
      heartbeatField.onChange(value === '' ? null : Number(value));
    }
  };

  return {
    actions: {
      handleGetLatestLogs,
      handleHeartbeatIntervalChange,
      handlePosTypeSelect,
      setPosTypeMenuOpen,
    },
    model: {
      hasSelectedOption,
      heartbeatError: heartbeatFieldState.error?.message,
      heartbeatField,
      logsFetchState,
      posTypeLabel,
      posTypeMenuOpen,
      readPathHasValue,
      selectedPosValue,
      showGilbarcoFields,
      showPosAgentFields,
      showScannerProxyFields,
      showVerifoneFields,
      writePathHasValue,
    },
  };
};
